// ============================================================
//  src/pages/bots/automations/FlowBuilderPage.js
//  Visual no-code automation builder — React Flow
//  npm install reactflow
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  addEdge, Background, Controls,
  useNodesState, useEdgesState,
  Handle, Position, Panel,
  MarkerType, getBezierPath, EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import {
  getAutomation, saveAutomation, listAutomations,
  activateAutomation, deactivateAutomation,
  getProducts,
} from '../../../api/client';
import { compileAutomation } from './compiler';
import './Automations.css';
import './FlowBuilder.css';

// ─────────────────────────────────────────────────────────────
//  NODE PALETTE DEFINITION
// ─────────────────────────────────────────────────────────────
export const NODE_PALETTE = [
  {
    category: 'Triggers',
    nodes: [
      {
        type: 'triggerNode', subtype: 'whatsapp_message',
        icon: '💬', label: 'WhatsApp Message', color: '#25D366',
        desc: 'Runs when a user sends a message matching a keyword or pattern.',
        howto: 'Set a keyword (e.g. "hi", "/start") or use * to match ANY message. When a user texts your bot with that word, this automation fires.',
        example: 'Keyword: "register" → starts your registration flow when user types "register".',
      },
      {
        type: 'triggerNode', subtype: 'whatsapp_button',
        icon: '🔘', label: 'Button Reply', color: '#007acc',
        desc: 'Runs when a user taps a specific quick-reply button.',
        howto: 'After sending a "Send Buttons" block, each button has an ID. Enter that exact button ID here. When the user taps it, this automation fires.',
        example: 'Button ID: "confirm_order" → triggers when user taps the "Confirm Order" button you sent them.',
      },
      {
        type: 'triggerNode', subtype: 'schedule',
        icon: '🕐', label: 'Schedule', color: '#f59e0b',
        desc: 'Runs automatically on a recurring time schedule.',
        howto: 'Choose a frequency (hourly, daily, weekly) or write a custom cron expression. The automation fires at those times — useful for daily digests, reminders, or reports.',
        example: 'Daily at 9:00 AM → send a morning broadcast to all users.',
      },
      {
        type: 'triggerNode', subtype: 'webhook',
        icon: '🔗', label: 'Incoming Webhook', color: '#8b5cf6',
        desc: 'Runs when an external system sends a request to a unique URL.',
        howto: 'Save the automation to generate a unique webhook URL. Paste that URL into any external tool (Stripe, Shopify, your own server). When that tool sends a POST request to it, this automation fires. The request body is available as {{webhook.field}}.',
        example: 'Shopify sends a POST when an order is placed → bot messages the customer on WhatsApp.',
      },
      {
        type: 'triggerNode', subtype: 'manual',
        icon: '▶',  label: 'Manual Trigger', color: '#6b7280',
        desc: 'Run this automation manually from the dashboard — for testing.',
        howto: 'No configuration needed. Click "Run Now" from the automations list to trigger it. Useful for testing your action sequence before going live.',
        example: 'Build and test your full flow here, then switch to a WhatsApp Message trigger when ready.',
      },
      {
        type: 'triggerNode', subtype: 'payment_received',
        icon: '💰', label: 'Payment Received', color: '#00C88C',
        desc: 'Runs the moment a customer successfully pays a Request Payment button — real confirmed payment, not just the button being sent.',
        howto: 'No separate setup here — this fires automatically whenever ANY "Request Payment" block in this bot gets paid. Build whatever you want to happen after payment: a custom thank-you message, a Google Sheets row, a Slack alert to you, etc. Use {{order.amount}}, {{order.description}}, {{order.id}}, and {{chatId}} anywhere in later steps. If you don\'t build one of these, customers still get a basic default "Payment received" text automatically — this trigger lets you customize or extend that.',
        example: 'Send Text: "✅ Thanks! We got your ₦{{order.amount}} payment for {{order.description}}." → Slack: notify #orders',
      },
    ],
  },
  {
    category: 'WhatsApp',
    nodes: [
      { type: 'actionNode', subtype: 'send_text',         icon: '💬', label: 'Send Text',          color: '#25D366', desc: 'Send a plain text message to the user. Supports variables like {{contact.name}}, {{storage.key}}, {{chatId}}.' },
      { type: 'actionNode', subtype: 'send_buttons',      icon: '🔘', label: 'Send Buttons',        color: '#25D366', desc: 'Send a message with up to 3 quick-reply buttons. Each button has a title and ID — connect each button\'s output to a different next step.' },
      { type: 'actionNode', subtype: 'send_buttons_image',icon: '🖼🔘',label: 'Buttons + Image',     color: '#25D366', desc: 'Send buttons with a header image. Attach an image URL and add up to 3 quick-reply buttons.' },
      { type: 'actionNode', subtype: 'send_cta_url',      icon: '🔗', label: 'CTA URL Button',      color: '#25D366', desc: 'Single button that opens an external link (e.g. "Sign Up/Login"). Cannot be combined with reply buttons — WhatsApp only allows one or the other.' },
      { type: 'actionNode', subtype: 'send_list',         icon: '📋', label: 'Send List',           color: '#25D366', desc: 'Send a scrollable list menu with multiple sections and rows. Each row can be connected to a different next step.' },
      { type: 'actionNode', subtype: 'ask_question',      icon: '❓', label: 'Ask Question',        color: '#0ea5e9', desc: 'Send a message and wait for the user\'s reply. The answer is saved as a variable you can use in later steps.' },
      { type: 'actionNode', subtype: 'send_flow',         icon: '⌥',  label: 'Send WA Flow',        color: '#9333ea', desc: 'Open an interactive WhatsApp Form Flow (requires a published WhatsApp Flow in your Meta dashboard).' },
      { type: 'actionNode', subtype: 'send_template',     icon: '📄', label: 'Send Template',       color: '#64748b', desc: 'Send a pre-approved WhatsApp message template. Required for messaging users outside the 24-hour window.' },
      { type: 'actionNode', subtype: 'send_media',        icon: '🖼',  label: 'Send Media',          color: '#ec4899', desc: 'Send an image, video, document, or audio file via URL.' },
    ],
  },
  {
    category: 'Logic',
    nodes: [
      { type: 'conditionNode', subtype: 'if_else',    icon: '🔀', label: 'If / Else',    color: '#f97316', desc: 'Split the flow into two paths based on a condition. "Yes" path runs if true, "No" path if false.' },
      { type: 'conditionNode', subtype: 'switch',     icon: '🔁', label: 'Switch',       color: '#f97316', desc: 'Split into multiple paths based on the value of a variable — like a menu router.' },
      { type: 'actionNode',    subtype: 'set_var',    icon: '🗄',  label: 'Set Variable', color: '#1abc9c', desc: 'Write a value into storage. Access it later as {{storage.key}} in any message or condition.' },
      { type: 'actionNode',    subtype: 'wait',       icon: '⏱',  label: 'Wait / Delay', color: '#94a3b8', desc: 'Pause the automation for N seconds before continuing to the next step.' },
      { type: 'actionNode',    subtype: 'loop',       icon: '🔄', label: 'Loop',         color: '#2980b9', desc: 'Repeat a set of actions for each item in a list (e.g. results from an API call).' },
      { type: 'endNode',       subtype: 'end',        icon: '🔚', label: 'End',          color: '#ef4444', desc: 'Explicitly stop the automation here. Useful to end one branch of an If/Else.' },
    ],
  },
  {
    category: 'Integrations',
    nodes: [
      { type: 'actionNode', subtype: 'send_email',    icon: '📧', label: 'Send Email',    color: '#3b82f6', desc: 'Send an email using your configured SMTP/PHPMailer settings.' },
      { type: 'actionNode', subtype: 'http_post',     icon: '🌐', label: 'HTTP Request',  color: '#6366f1', desc: 'Make a GET or POST request to any URL. Save the response as a variable for later steps.' },
      { type: 'actionNode', subtype: 'google_sheets', icon: '📊', label: 'Google Sheets', color: '#16a34a', desc: 'Append a row to or read from a Google Sheet. Great for lead capture and data logging.' },
      { type: 'actionNode', subtype: 'airtable',      icon: '🟦', label: 'Airtable',      color: '#0ea5e9', desc: 'Create or update a record in an Airtable base.' },
      { type: 'actionNode', subtype: 'slack',         icon: '💼', label: 'Slack',         color: '#4a154b', desc: 'Post a message to a Slack channel via an incoming webhook URL.' },
      { type: 'actionNode', subtype: 'openai',        icon: '🤖', label: 'AI / OpenAI',   color: '#10a37f', desc: 'Generate text with GPT. Save the AI reply as a variable to send in WhatsApp.' },
    ],
  },
  {
    category: 'Commerce',
    nodes: [
      {
        type: 'actionNode', subtype: 'request_payment',
        icon: '💳', label: 'Request Payment', color: '#00C88C',
        desc: 'Send a real "Pay" button in the chat for a saved product or a custom amount. Money goes straight to your own PocketFi account — CmdKit never touches it.',
        howto: 'Connect your PocketFi account first, in the Commerce tab (outside this builder). Then pick a saved product, or switch to "Custom amount" to charge a one-off price without adding it to your catalog. When the customer taps Pay and completes payment, the bot automatically confirms in the same chat.',
        example: 'Product: "Blue Sneakers" → sends "Pay ₦15,000" button. Customer pays → bot replies "✅ Payment received!"',
      },
    ],
  },
];

const ALL_NODES    = NODE_PALETTE.flatMap(c => c.nodes);
const NODE_COLOR   = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.color]));
const NODE_ICON    = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.icon]));
const NODE_LABEL   = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.label]));
const NODE_DESC    = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.desc]));
const NODE_HOWTO   = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.howto ?? '']));
const NODE_EXAMPLE = Object.fromEntries(ALL_NODES.map(n => [n.subtype, n.example ?? '']));

// ─────────────────────────────────────────────────────────────
//  CUSTOM NODE COMPONENTS
// ─────────────────────────────────────────────────────────────
function NodeShell({ id, data, selected, children, hasInput = true, outputHandles = ['default'] }) {
  const subtype   = data.subtype ?? data.triggerType ?? 'unknown';
  const color     = NODE_COLOR[subtype]  ?? '#94a3b8';
  const icon      = NODE_ICON[subtype]   ?? '•';
  const label     = NODE_LABEL[subtype]  ?? data.label ?? 'Node';
  const isTrigger = !hasInput;
  const hasNamed  = outputHandles.length > 0 && outputHandles[0] !== 'default';

  return (
    <div
      className={`fb-node${selected ? ' fb-node-selected' : ''}${isTrigger ? ' fb-node-trigger' : ''}`}
      style={{ '--node-color': color }}
    >
      {hasInput && (
        <Handle type="target" position={Position.Top} id="in"
          className="fb-handle fb-handle-in" />
      )}

      <div className="fb-node-header">
        <span className="fb-node-icon">{icon}</span>
        <span className="fb-node-label">{label}</span>
      </div>

      {children && <div className="fb-node-body">{children}</div>}

      {/* Single default output */}
      {!hasNamed && outputHandles.length >= 1 && (
        <Handle type="source" position={Position.Bottom} id="out"
          className="fb-handle fb-handle-out" />
      )}

      {/* Named outputs — rendered as label rows INSIDE the node, with handles
          at Position.Bottom spread via left% so React Flow finds them correctly.
          The handles are siblings to fb-named-outputs (not children of it) so
          React Flow measures their position against the node root, not the row div. */}
      {hasNamed && (
        <>
          <div className="fb-named-outputs">
            {outputHandles.map((h) => (
              <div key={h} className="fb-output-row">
                <span className="fb-output-label">{h}</span>
                <span className="fb-output-arrow">↓</span>
              </div>
            ))}
          </div>
          {/* Handles OUTSIDE the scrollable/padded div so RF measures from node root */}
          {outputHandles.map((h, i) => (
            <Handle
              key={h}
              type="source"
              position={Position.Bottom}
              id={h}
              className="fb-handle fb-handle-named-bottom"
              style={{
                left: `${((i + 0.5) / outputHandles.length) * 100}%`,
                bottom: -7,
                transform: 'translateX(-50%)',
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}

function TriggerNode({ id, data, selected }) {
  const sub = data.triggerType ?? data.subtype;
  return (
    <NodeShell id={id} data={{ ...data, subtype: sub }} selected={selected} hasInput={false}>
      {data.config?.keyword   && <div className="fb-node-detail">Keyword: <strong>{data.config.keyword}</strong></div>}
      {data.config?.frequency && <div className="fb-node-detail">Every: <strong>{data.config.frequency}</strong></div>}
      {sub === 'webhook'      && <div className="fb-node-detail fb-detail-muted">Unique URL generated on save</div>}
      {sub === 'manual'       && <div className="fb-node-detail fb-detail-muted">Run from dashboard</div>}
    </NodeShell>
  );
}

function ActionNode({ id, data, selected }) {
  const cfg = data.config ?? {};
  // For button/list nodes derive named outputs from config
  let outputs = ['default'];
  if (data.subtype === 'send_buttons' || data.subtype === 'send_buttons_image') {
    const btns = cfg.buttons ?? [];
    if (btns.length > 0) outputs = btns.map(b => b.title || b.id || `Btn ${b._idx ?? '?'}`);
  }
  if (data.subtype === 'send_list') {
    const rows = (cfg.sections ?? []).flatMap(s => s.rows ?? []);
    if (rows.length > 0) outputs = rows.map(r => r.title || r.id || 'Row');
  }

  return (
    <NodeShell id={id} data={data} selected={selected} outputHandles={outputs}>
      {cfg.text     && <div className="fb-node-detail fb-preview-text">{cfg.text.slice(0, 60)}{cfg.text.length > 60 ? '…' : ''}</div>}
      {cfg.body     && <div className="fb-node-detail fb-preview-text">{cfg.body.slice(0, 60)}{cfg.body.length > 60 ? '…' : ''}</div>}
      {cfg.question && <div className="fb-node-detail fb-preview-text">{cfg.question.slice(0, 60)}</div>}
      {cfg.subject  && <div className="fb-node-detail">Subject: <strong>{cfg.subject}</strong></div>}
      {cfg.url      && <div className="fb-node-detail fb-preview-url">{cfg.url.slice(0, 36)}…</div>}
      {cfg.save_as  && <div className="fb-node-detail">→ <code>{cfg.save_as}</code></div>}
      {cfg.key      && <div className="fb-node-detail"><code>{cfg.key}</code> = {String(cfg.value ?? '').slice(0,20)}</div>}
      {(data.subtype === 'send_buttons' || data.subtype === 'send_buttons_image') && (cfg.buttons ?? []).length > 0 && (
        <div className="fb-node-chips">
          {(cfg.buttons ?? []).map((b,i) => <span key={i} className="fb-btn-chip">{b.title}</span>)}
        </div>
      )}
      {data.subtype === 'send_cta_url' && cfg.display_text && (
        <div className="fb-node-chips">
          <span className="fb-btn-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>🔗 {cfg.display_text}</span>
        </div>
      )}
      {data.subtype === 'send_list' && (cfg.sections ?? []).length > 0 && (
        <div className="fb-node-detail fb-detail-muted">{(cfg.sections ?? []).flatMap(s=>s.rows??[]).length} options</div>
      )}
      {data.subtype === 'request_payment' && (
        <div className="fb-node-chips">
          <span className="fb-btn-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            💳 {cfg.mode === 'custom'
              ? (cfg.amount ? `₦${Number(cfg.amount).toLocaleString('en-NG')}` : 'Custom amount — not set')
              : (cfg.product_name || (cfg.product_id ? cfg.product_id : 'No product selected'))}
          </span>
        </div>
      )}
    </NodeShell>
  );
}

function ConditionNode({ id, data, selected }) {
  const cfg     = data.config ?? {};
  const outputs = data.subtype === 'switch'
    ? [...(cfg.cases ?? [{ label: 'Case 1' }]).map(c => c.label || c.value || 'Case'), 'Default']
    : ['✓ Yes', '✗ No'];

  return (
    <NodeShell id={id} data={data} selected={selected} outputHandles={outputs}>
      {cfg.left && (
        <div className="fb-node-detail">
          <code>{cfg.left}</code> <span className="fb-op">{cfg.op ?? '=='}</span> <code>{cfg.right ?? ''}</code>
        </div>
      )}
    </NodeShell>
  );
}

function EndNode({ id, data, selected }) {
  return <NodeShell id={id} data={{ ...data, subtype: 'end' }} selected={selected} hasInput outputHandles={[]}>{null}</NodeShell>;
}

const NODE_TYPES = {
  triggerNode:   TriggerNode,
  actionNode:    ActionNode,
  conditionNode: ConditionNode,
  endNode:       EndNode,
};

const EDGE_OPTS = {
  type: 'deletable',
  animated: true,
  markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
  style:     { stroke: '#94a3b8', strokeWidth: 2 },
};

// ── Deletable edge — hover the connection to reveal an ✕ button that
// removes it, instead of having to select the thin line + press Delete.
function DeletableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data }) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });

  return (
    <>
      {/* Wide invisible hit-zone — React Flow only makes the visible
          2px path itself hoverable by default, which is unreliably thin
          to aim for. This transparent, much-thicker path sits underneath
          and captures hover/click instead, so the delete button reliably
          appears anywhere near the line, not just exactly on it. */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        style={{ cursor: 'pointer' }}
        className="fb-edge-hit-area"
      />
      <path id={id} className="react-flow__edge-path" d={edgePath} style={style} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <button
          className="fb-edge-delete-btn"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          onClick={(e) => { e.stopPropagation(); data?.onDelete?.(id); }}
          title="Remove connection"
        >
          ✕
        </button>
      </EdgeLabelRenderer>
    </>
  );
}

const EDGE_TYPES = { deletable: DeletableEdge };

// ── Auto-layout — arranges nodes into clean top-to-bottom layers by
// BFS distance from the trigger node, spacing each layer evenly.
// No external layout library required (keeps this a drop-in change).
function autoLayoutNodes(nodes, edges) {
  if (nodes.length === 0) return nodes;

  const outgoing = {};
  edges.forEach(e => { (outgoing[e.source] ??= []).push(e.target); });

  const trigger = nodes.find(n => n.type === 'triggerNode');
  const depth = {};
  const queue = trigger ? [[trigger.id, 0]] : [];
  const seen = new Set(queue.map(([id]) => id));

  while (queue.length) {
    const [id, d] = queue.shift();
    depth[id] = Math.max(depth[id] ?? 0, d);
    (outgoing[id] ?? []).forEach(next => {
      if (!seen.has(next) || depth[next] < d + 1) {
        seen.add(next);
        queue.push([next, d + 1]);
      }
    });
  }

  // Any node not reached from the trigger (orphaned / disconnected) gets
  // its own layer at the end rather than being silently skipped.
  let maxDepth = Math.max(0, ...Object.values(depth));
  nodes.forEach(n => { if (!(n.id in depth)) depth[n.id] = ++maxDepth; });

  const layers = {};
  nodes.forEach(n => { (layers[depth[n.id]] ??= []).push(n.id); });

  const COL_GAP = 260;
  const ROW_GAP = 160;
  const positions = {};
  Object.keys(layers).sort((a, b) => a - b).forEach(layerKey => {
    const ids = layers[layerKey];
    const totalWidth = (ids.length - 1) * COL_GAP;
    ids.forEach((id, i) => {
      positions[id] = { x: i * COL_GAP - totalWidth / 2 + 400, y: Number(layerKey) * ROW_GAP + 80 };
    });
  });

  return nodes.map(n => ({ ...n, position: positions[n.id] ?? n.position }));
}

// ─────────────────────────────────────────────────────────────
//  LEFT PALETTE
// ─────────────────────────────────────────────────────────────
function NodePalette({ onInfo }) {
  const [search, setSearch] = useState('');
  const filtered = NODE_PALETTE.map(cat => ({
    ...cat,
    nodes: cat.nodes.filter(n =>
      !search || n.label.toLowerCase().includes(search.toLowerCase()) ||
      n.desc.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.nodes.length > 0);

  function onDragStart(e, n) {
    e.dataTransfer.setData('ck/type',    n.type);
    e.dataTransfer.setData('ck/subtype', n.subtype);
    e.dataTransfer.setData('ck/label',   n.label);
    e.dataTransfer.effectAllowed = 'move';
  }

  return (
    <div className="fb-palette">
      <div className="fb-palette-header">
        <div className="fb-palette-title">Blocks</div>
        <input className="fb-palette-search" placeholder="Search blocks…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="fb-palette-body">
        {filtered.map(cat => (
          <div key={cat.category} className="fb-palette-category">
            <div className="fb-palette-cat-label">{cat.category}</div>
            {cat.nodes.map(n => (
              <div key={n.subtype} className="fb-palette-node"
                draggable onDragStart={e => onDragStart(e, n)}>
                <span className="fb-palette-node-icon" style={{ color: n.color }}>{n.icon}</span>
                <div className="fb-palette-node-text">
                  <div className="fb-palette-node-label">{n.label}</div>
                  <div className="fb-palette-node-desc">{n.desc.slice(0, 55)}{n.desc.length > 55 ? '…' : ''}</div>
                </div>
                {(n.howto || n.example) && (
                  <button className="fb-palette-info-btn" title="How it works"
                    onClick={e => { e.stopPropagation(); onInfo(n); }}>?</button>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  BLOCK INFO MODAL
// ─────────────────────────────────────────────────────────────
function BlockInfoModal({ node, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title" style={{ color: node.color }}>
            {node.icon} {node.label}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="fb-info-section-label">What it does</div>
            <p style={{ fontSize: 13.5, color: 'var(--text-dark)', margin: 0, lineHeight: 1.6 }}>{node.desc}</p>
          </div>
          {node.howto && (
            <div>
              <div className="fb-info-section-label">How to use it</div>
              <p style={{ fontSize: 13.5, color: 'var(--text-dark)', margin: 0, lineHeight: 1.6 }}>{node.howto}</p>
            </div>
          )}
          {node.example && (
            <div style={{ background: 'var(--primary-light, rgba(0,44,237,0.06))', border: '1.5px solid var(--primary-border, rgba(0,44,237,0.15))', borderRadius: 10, padding: '10px 14px' }}>
              <div className="fb-info-section-label" style={{ marginBottom: 4 }}>Example</div>
              <p style={{ fontSize: 13, color: 'var(--text-dark)', margin: 0, lineHeight: 1.6 }}>{node.example}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  HOW TO USE AUTOMATION GUIDE MODAL
// ─────────────────────────────────────────────────────────────
function HowToModal({ onClose }) {
  const steps = [
    {
      num: '1', title: 'Pick a Trigger',
      desc: 'Every automation starts with a trigger — the event that kicks it off. Drag a Trigger block onto the canvas and configure it. Example: "WhatsApp Message" with keyword "register".',
      example: '💬 User types "register" → automation fires',
    },
    {
      num: '2', title: 'Add Action Blocks',
      desc: 'Drag action blocks from the left panel and connect them to your trigger by dragging from the green dot at the bottom to the top of the next block. Actions run in order, top to bottom.',
      example: '→ Ask Question: "What is your name?" → Save as: user_name',
    },
    {
      num: '3', title: 'Use Variables',
      desc: 'Data collected in one step is available in all later steps using {{variable}} syntax. User replies saved by Ask Question become {{storage.key}}. API responses become {{save_as.field}}.',
      example: '"Hello {{storage.user_name}}, your ID is {{chatId}}"',
    },
    {
      num: '4', title: 'Add Conditions',
      desc: 'Use If/Else or Switch blocks to split the flow into different paths based on data. Connect the "Yes" and "No" outputs to different next steps.',
      example: 'If {{storage.plan}} == "premium" → Send premium message\nElse → Send upgrade prompt',
    },
    {
      num: '5', title: 'Connect Integrations',
      desc: 'Use HTTP Request, Google Sheets, Airtable, or Email blocks to connect with external services. Responses are saved as variables for use in later steps.',
      example: 'HTTP POST to your API → save result → Send text with {{result.balance}}',
    },
    {
      num: '6', title: 'Save & Activate',
      desc: 'Click Save to store your work as a draft. When ready, click Activate — the automation goes live and starts responding to real users immediately.',
      example: 'Draft → test it → Activate → live on WhatsApp',
    },
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div className="modal-title">🧭 How to Build an Automation</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>

          {/* Case study */}
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, color: '#15803d', marginBottom: 6 }}>📋 Case Study: Lead Capture Bot</div>
            <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.7 }}>
              A business wants to collect name + email from WhatsApp users.<br/>
              <strong>Trigger:</strong> WhatsApp Message → keyword "*" (any message)<br/>
              <strong>Step 1:</strong> Ask Question → "What's your name?" → save as <code>user_name</code><br/>
              <strong>Step 2:</strong> Ask Question → "What's your email?" → save as <code>user_email</code><br/>
              <strong>Step 3:</strong> Google Sheets → append row [name, email, phone, timestamp]<br/>
              <strong>Step 4:</strong> Send Email → notify admin<br/>
              <strong>Step 5:</strong> Send Text → "Thanks {'{{storage.user_name}}'}! We'll be in touch."
            </div>
          </div>

          {steps.map(s => (
            <div key={s.num} style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 800, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {s.num}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-dark)', marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 6 }}>{s.desc}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', color: 'var(--primary)', whiteSpace: 'pre-line' }}>
                  {s.example}
                </div>
              </div>
            </div>
          ))}

          <div style={{ background: 'rgba(255,149,0,0.06)', border: '1.5px solid rgba(255,149,0,0.25)', borderRadius: 10, padding: '12px 14px', marginTop: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#92400e', marginBottom: 4 }}>💡 Pro Tips</div>
            <ul style={{ fontSize: 12.5, color: '#92400e', margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Use <code>{'{{chatId}}'}</code> to get the user's WhatsApp number</li>
              <li>Use <code>{'{{contact.name}}'}</code> for their WhatsApp display name</li>
              <li>Use <code>{'{{now}}'}</code> for the current timestamp</li>
              <li>Button/List outputs can each connect to different next steps</li>
              <li>Always test with Manual Trigger before activating</li>
            </ul>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Start Building</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  NODE CONFIG PANEL (right sidebar)
// ─────────────────────────────────────────────────────────────
// ── Stable field components ───────────────────────────────────────────────────
// MUST live outside NodeConfigPanel. If defined inside, React creates a new
// component type on every parent render → unmounts/remounts the input →
// focus is lost after every single keystroke.
function CfgField({ cfg, set, label, k, placeholder, type = 'text', hint }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input type={type} className="form-input" placeholder={placeholder}
        value={cfg[k] ?? ''} onChange={e => set(k, e.target.value)} />
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}
function CfgTextarea({ cfg, set, label, k, placeholder, rows = 3 }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <textarea className="form-input" placeholder={placeholder} rows={rows}
        style={{ resize: 'vertical' }} value={cfg[k] ?? ''} onChange={e => set(k, e.target.value)} />
    </div>
  );
}

// Same id WhatsApp actually returns in button_reply.id / list_reply.id —
// must match compiler.js's slugify exactly, or the If/Else dropdown will
// build conditions that never match a real reply.
function slugify(str) {
  return (str ?? '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

// ── Pulls every Button/List option out of a canvas's nodes, tagged with
// where it came from — shared by ButtonTriggerPicker (cross-automation)
// and IfElseConfig (same-canvas only, see allOptions below).
function extractButtonOptions(nodes, sourceLabel) {
  const opts = [];
  (nodes ?? []).forEach(n => {
    const sub = n.data?.subtype;
    if (sub === 'send_buttons' || sub === 'send_buttons_image') {
      (n.data?.config?.buttons ?? []).filter(b => b.title).forEach(b => {
        opts.push({ id: b.id || slugify(b.title), title: b.title, kind: 'Button', source: sourceLabel });
      });
    }
    if (sub === 'send_list') {
      (n.data?.config?.sections ?? []).flatMap(s => s.rows ?? []).filter(r => r.title).forEach(r => {
        opts.push({ id: r.id || slugify(r.title), title: r.title, kind: 'List row', source: sourceLabel });
      });
    }
  });
  return opts;
}

// ── ButtonTriggerPicker — used by the whatsapp_button trigger config.
// Scans EVERY automation on this bot (not just the current canvas) for
// Send Buttons / Send List blocks, and lets the person pick the exact
// ID by title instead of typing a raw, easy-to-typo keyword blind.
// MUST be outside NodeConfigPanel (stable reference, same reason as
// IfElseConfig/CfgField above).
function ButtonTriggerPicker({ cfg, set, bId, aId, currentCanvasNodes }) {
  const [options, setOptions] = useState(() => extractButtonOptions(currentCanvasNodes, 'This automation'));
  const [loading, setLoading] = useState(false);
  const [loaded,  setLoaded]  = useState(false);
  const [search,  setSearch]  = useState('');
  const [manualOverride, setManualOverride] = useState(false);

  // Fetch every OTHER automation's canvas once per panel session — not on
  // every keystroke — same "fetch once, reuse" pattern as the Request
  // Payment node's product list above.
  useEffect(() => {
    if (loaded || !bId) return;
    setLoading(true);
    listAutomations(bId)
      .then(res => {
        const others = (res.data?.automations ?? []).filter(a => a.aId !== aId);
        return Promise.all(others.map(a =>
          getAutomation(bId, a.aId)
            .then(r => extractButtonOptions(r.data?.automation?.aCanvas?.nodes, a.aName))
            .catch(() => [])
        ));
      })
      .then(lists => {
        const own = extractButtonOptions(currentCanvasNodes, 'This automation');
        const all = [...own, ...lists.flat()];
        // De-dupe by id — the current canvas's own copy wins since it
        // reflects unsaved edits, other automations' copies come from
        // whatever was last saved.
        const seen = new Set();
        setOptions(all.filter(o => (seen.has(o.id) ? false : (seen.add(o.id), true))));
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setLoaded(true); });
  }, [bId, aId, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = options.filter(o =>
    !search || o.title.toLowerCase().includes(search.toLowerCase()) || o.id.toLowerCase().includes(search.toLowerCase())
  );

  // "Manual" mode is implicit if the saved keyword doesn't match any known
  // option (e.g. it points at a button from the older, non-automation
  // Commands system, which this picker doesn't scan) — or explicit if the
  // person deliberately clicked "Type the ID manually".
  const isManual = manualOverride || (!!cfg.keyword && !options.some(o => o.id === cfg.keyword));

  function selectOpt(opt) {
    setManualOverride(false);
    set('keyword', opt.id);
  }

  return (
    <div className="form-group">
      <label className="form-label" style={{ marginBottom: 8 }}>Which button or list row starts this?</label>

      <input className="form-input" placeholder="Search buttons & list rows…" value={search}
        onChange={e => setSearch(e.target.value)} style={{ marginBottom: 8 }} />

      {loading && <div className="form-hint">Scanning your bot's automations…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ fontSize: 12.5, color: '#92400e', background: 'rgba(255,149,0,0.08)', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid rgba(255,149,0,0.5)' }}>
          💡 {search ? 'No buttons or list rows match your search.' : 'No buttons or list rows found yet on this bot.'} Add a Send Buttons or Send List block somewhere first, or type the ID manually below.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', marginBottom: 4 }}>
          {filtered.map((opt, i) => {
            const isSelected = !isManual && cfg.keyword === opt.id;
            return (
              <button key={`${opt.id}_${i}`} type="button" onClick={() => selectOpt(opt)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
                  border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                  background: isSelected ? 'var(--primary-light, rgba(0,44,237,0.06))' : '#fff',
                  textAlign: 'left', width: '100%', transition: 'all 0.12s',
                }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{opt.kind === 'Button' ? '🔘' : '☰'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {opt.kind} · {opt.source} · id: <code>{opt.id}</code>
                  </div>
                </div>
                {isSelected && <span style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}

      <button type="button" onClick={() => setManualOverride(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
          border: isManual ? '2px solid var(--primary)' : '1.5px dashed var(--border)',
          background: isManual ? 'var(--primary-light, rgba(0,44,237,0.06))' : 'transparent',
          textAlign: 'left', width: '100%', marginTop: 4,
        }}>
        <span style={{ fontSize: 16 }}>✏️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)' }}>Type the ID manually</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>For buttons from your regular Commands, not Automations — this picker only scans Automations</div>
        </div>
        {isManual && <span style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 16 }}>✓</span>}
      </button>

      {isManual && (
        <div style={{ marginTop: 8 }}>
          <CfgField cfg={cfg} set={set} label="Button ID" k="keyword" placeholder="confirm_order"
            hint="Must exactly match the button/row ID sent by whatever block generated it." />
        </div>
      )}

      <div className="fb-config-howto" style={{ marginTop: 10 }}>
        💡 When a customer taps a button or list row, WhatsApp sends its ID back as a message — this trigger fires when that ID matches. Pick it from the list above instead of typing it blind.
      </div>
    </div>
  );
}

// ── IfElseConfig — MUST be outside NodeConfigPanel (stable reference) ────────
function IfElseConfig({ cfg, set, setMany, allNodes }) {
  // Build flat list of all selectable options from the canvas
  const allOptions = [];

  allNodes.filter(n => n.data?.subtype === 'send_buttons' || n.data?.subtype === 'send_buttons_image').forEach(n => {
    (n.data?.config?.buttons ?? []).filter(b => b.title).forEach(b => {
      const id = b.id || slugify(b.title);
      allOptions.push({ key: `${n.id}::${b.title}`, srcLabel: 'Button', srcIcon: '🔘', variable: '{{message}}', value: id });
    });
  });

  allNodes.filter(n => n.data?.subtype === 'send_list').forEach(n => {
    (n.data?.config?.sections ?? []).flatMap(s => s.rows ?? []).filter(r => r.title).forEach(r => {
      const id = r.id || slugify(r.title);
      allOptions.push({ key: `${n.id}::${r.title}`, srcLabel: 'List', srcIcon: '📋', variable: '{{message}}', value: id });
    });
  });

  allNodes.filter(n => n.data?.subtype === 'ask_question' && n.data?.config?.save_as).forEach(n => {
    const saveAs = n.data.config.save_as;
    const variable = `{{storage.${saveAs}}}`;
    const rows = allNodes
      .filter(x => x.data?.subtype === 'send_list')
      .flatMap(x => (x.data?.config?.sections ?? []).flatMap(s => s.rows ?? []))
      .filter(r => r.title);
    if (rows.length > 0) {
      rows.forEach(r => {
        const id = r.id || slugify(r.title);
        allOptions.push({ key: `${n.id}::${r.title}`, srcLabel: `Saved as "${saveAs}"`, srcIcon: '💾', variable, value: id });
      });
    } else {
      allOptions.push({ key: n.id, srcLabel: `Saved as "${saveAs}"`, srcIcon: '💾', variable, value: '' });
    }
  });

  allNodes.filter(n => n.data?.subtype === 'set_var' && n.data?.config?.key).forEach(n => {
    allOptions.push({ key: n.id, srcLabel: 'Variable', srcIcon: '📦', variable: `{{storage.${n.data.config.key}}}`, value: '' });
  });

  const selectedKey  = cfg._optKey ?? '';
  const isManual     = cfg._manual === true;
  const selectedOpt  = allOptions.find(o => o.key === selectedKey) ?? null;
  const needsRight   = (cfg.op ?? '==') !== 'is_empty' && (cfg.op ?? '==') !== 'is_not_empty';

  function selectOpt(opt) {
    setMany({ _optKey: opt.key, _manual: false, left: opt.variable, right: opt.value, op: cfg.op || '==' });
  }

  function selectManual() {
    setMany({ _optKey: '', _manual: true, left: '', right: '' });
  }

  return (
    <>
      <div className="form-group">
        <label className="form-label" style={{ marginBottom: 8 }}>When user selects…</label>

        {allOptions.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#92400e', background: 'rgba(255,149,0,0.08)', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid rgba(255,149,0,0.5)' }}>
            💡 Add a <strong>Send Buttons</strong> or <strong>Send List</strong> block first — its options will appear here automatically.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allOptions.map(opt => {
              const isSelected = !isManual && selectedKey === opt.key;
              return (
                <button key={opt.key} type="button" onClick={() => selectOpt(opt)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
                    border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                    background: isSelected ? 'var(--primary-light, rgba(0,44,237,0.06))' : '#fff',
                    textAlign: 'left', width: '100%', transition: 'all 0.12s',
                  }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{opt.srcIcon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.value || opt.srcLabel}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opt.srcLabel}</div>
                  </div>
                  {isSelected && <span style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 16 }}>✓</span>}
                </button>
              );
            })}

            <button type="button" onClick={selectManual}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
                border: isManual ? '2px solid var(--primary)' : '1.5px dashed var(--border)',
                background: isManual ? 'var(--primary-light, rgba(0,44,237,0.06))' : 'transparent',
                textAlign: 'left', width: '100%',
              }}>
              <span style={{ fontSize: 18 }}>✏️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)' }}>Type a custom value</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Use a variable or any text</div>
              </div>
              {isManual && <span style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 16 }}>✓</span>}
            </button>
          </div>
        )}
      </div>

      {isManual && (
        <CfgField cfg={cfg} set={set} label="Checking this value" k="left" placeholder="{{storage.answer}} or {{message}}" />
      )}

      {!isManual && selectedOpt && (
        <div style={{ fontSize: 12, color: '#166534', background: 'rgba(22,101,52,0.07)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, borderLeft: '3px solid #16a34a' }}>
          ✅ If reply is <strong>"{selectedOpt.value}"</strong> → <strong>Yes</strong> path runs
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Condition</label>
        <select className="form-input" value={cfg.op ?? '=='} onChange={e => set('op', e.target.value)}>
          <option value="==">is equal to (exact match)</option>
          <option value="!=">is NOT equal to</option>
          <option value="contains">contains</option>
          <option value="starts_with">starts with</option>
          <option value="ends_with">ends with</option>
          <option value=">">is greater than (number)</option>
          <option value="<">is less than (number)</option>
          <option value=">=">is greater than or equal to (number)</option>
          <option value="<=">is less than or equal to (number)</option>
          <option value="is_empty">is empty / not set</option>
          <option value="is_not_empty">is not empty</option>
        </select>
        <div className="form-hint">
          {needsRight
            ? 'Both sides support variables like {{message}} or {{storage.key}}.'
            : 'No "compare to" value needed — this only checks whether the left side has anything in it.'}
        </div>
      </div>

      {isManual && needsRight && (
        <CfgField cfg={cfg} set={set} label="Compare to" k="right" placeholder="e.g. 500 or yes" />
      )}

      <ConditionTester cfg={cfg} />
    </>
  );
}

// ── ConditionTester — lets a builder verify branching logic before saving,
// instead of discovering a bad condition only after a real customer hits it.
// Mirrors ActionRunner::execIfElse() in engine/Runtime/ActionRunner.php exactly —
// keep both in sync if operators change.
function evalOpForPreview(left, op, right) {
  const l = String(left ?? '').trim();
  const r = String(right ?? '').trim();
  switch (op) {
    case '==':          return l === r;
    case '!=':          return l !== r;
    case '>':           return parseFloat(left) > parseFloat(right);
    case '<':           return parseFloat(left) < parseFloat(right);
    case '>=':          return parseFloat(left) >= parseFloat(right);
    case '<=':          return parseFloat(left) <= parseFloat(right);
    case 'contains':    return String(left ?? '').includes(String(right ?? ''));
    case 'starts_with': return String(left ?? '').startsWith(String(right ?? ''));
    case 'ends_with':   return String(left ?? '').endsWith(String(right ?? ''));
    case 'is_empty':    return l === '';
    case 'is_not_empty':return l !== '';
    default:            return false;
  }
}

function ConditionTester({ cfg }) {
  const [testVal, setTestVal] = useState('');
  const op = cfg.op ?? '==';
  const needsRight = op !== 'is_empty' && op !== 'is_not_empty';
  const hasEnoughToTest = (cfg.left ?? '') !== '' && (!needsRight || (cfg.right ?? '') !== '');
  const result = hasEnoughToTest ? evalOpForPreview(testVal, op, cfg.right) : null;

  return (
    <div className="form-group" style={{ marginTop: 4 }}>
      <label className="form-label">🧪 Test this condition</label>
      <div className="form-hint" style={{ marginBottom: 6 }}>
        Type a sample value for <code>{cfg.left || '(left side)'}</code> to see which path fires — nothing is sent, this is just a preview.
      </div>
      <input
        className="form-input"
        placeholder="Type a sample reply/value…"
        value={testVal}
        onChange={e => setTestVal(e.target.value)}
        disabled={!hasEnoughToTest}
      />
      {!hasEnoughToTest && (
        <div className="form-hint">Set up the condition above first.</div>
      )}
      {hasEnoughToTest && (
        <div style={{
          marginTop: 8, fontSize: 12.5, fontWeight: 700, padding: '8px 12px', borderRadius: 8,
          color: result ? '#166534' : '#92400e',
          background: result ? 'rgba(22,101,52,0.08)' : 'rgba(255,149,0,0.08)',
          borderLeft: `3px solid ${result ? '#16a34a' : 'rgba(255,149,0,0.5)'}`,
        }}>
          {result ? '✅ Yes path would run' : '➡️ No path would run'}
        </div>
      )}
    </div>
  );
}

function NodeConfigPanel({ node, onChange, onDelete, onClose, allNodes = [], bId, aId }) {
  const [cfg, setCfg] = useState(node.data.config ?? {});
  const nodeIdRef     = useRef(node.id);
  const subtype = node.data.subtype ?? node.data.triggerType;

  // Products are only needed for the Request Payment node, and only
  // fetched once per panel session — not on every keystroke.
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  useEffect(() => {
    if (subtype !== 'request_payment' || productsLoaded || !bId) return;
    getProducts(bId)
      .then(res => setProducts(res.data?.products ?? []))
      .catch(() => {})
      .finally(() => setProductsLoaded(true));
  }, [subtype, bId, productsLoaded]);

  // Only re-initialise cfg when the user selects a DIFFERENT node.
  // Without this guard, every keystroke updates parent `nodes` state →
  // NodeConfigPanel receives a new `node` prop → useState re-runs → cfg resets
  // → input loses focus / only one char types.
  useEffect(() => {
    if (node.id !== nodeIdRef.current) {
      nodeIdRef.current = node.id;
      setCfg(node.data.config ?? {});
    }
  }, [node.id, node.data.config]);

  function set(k, v) {
    const next = { ...cfg, [k]: v };
    setCfg(next);
    onChange(node.id, next);
  }

  // Batch-update multiple keys at once — avoids stale-closure overwrite
  // when multiple set() calls would each close over the same old cfg.
  function setMany(updates) {
    const next = { ...cfg, ...updates };
    setCfg(next);
    onChange(node.id, next);
  }

  const color = NODE_COLOR[subtype] ?? '#6b7280';
  const icon  = NODE_ICON[subtype]  ?? '•';
  const label = NODE_LABEL[subtype] ?? subtype;
  const desc  = NODE_DESC[subtype]  ?? '';
  const howto = NODE_HOWTO[subtype] ?? '';

  // Shorthand aliases — these are NOT new component definitions, just
  // references to the stable module-level CfgField / CfgTextarea components.
  const F = CfgField;
  const T = CfgTextarea;

  // ── Button helper — plain function (NOT a component) so React never
  //    unmounts/remounts inputs when the parent re-renders on each keystroke.
  const renderButtonsBuilder = () => {
    const btns = cfg.buttons ?? [];
    return (
      <div className="form-group">
        <label className="form-label">Buttons (max 3) — each button output can connect to a different next step</label>
        {btns.map((btn, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11, fontWeight: 800, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>{i+1}</div>
            <input className="form-input" placeholder={`Button ${i+1} label`} maxLength={20}
              value={btn.title ?? ''} onChange={e => {
                const next = [...btns];
                next[i] = { ...btn, title: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g,'_') };
                set('buttons', next);
              }} />
            {btns.length > 1 && (
              <button className="icon-btn danger" onClick={() => set('buttons', btns.filter((_,j)=>j!==i))}>✕</button>
            )}
          </div>
        ))}
        {btns.length < 3 && (
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
            onClick={() => set('buttons', [...btns, { id: `btn${Date.now()}`, title: '' }])}>
            + Add Button
          </button>
        )}
        <div className="form-hint">After adding buttons, save — the node will show output connectors for each button. Connect each to the next step you want.</div>
      </div>
    );
  };

  // ── List sections builder — plain function for the same reason
  const renderListSectionsBuilder = () => {
    const sections = cfg.sections ?? [{ title: 'Options', rows: [{ id: 'opt1', title: '' }] }];
    return (
      <div className="form-group">
        <label className="form-label">Sections & Rows — each row output can connect to a different next step</label>
        {sections.map((sec, si) => (
          <div key={si} style={{ border: '1.5px solid var(--border)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input className="form-input" placeholder="Section title (e.g. Main Menu)"
                value={sec.title ?? ''} onChange={e => {
                  const next = [...sections];
                  next[si] = { ...sec, title: e.target.value };
                  set('sections', next);
                }} />
              {sections.length > 1 && (
                <button className="icon-btn danger" onClick={() => set('sections', sections.filter((_,j)=>j!==si))}>✕</button>
              )}
            </div>
            {(sec.rows ?? []).map((row, ri) => (
              <div key={ri} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input className="form-input" placeholder={`Row ${ri+1} title`}
                  value={row.title ?? ''} onChange={e => {
                    const next = [...sections];
                    next[si] = { ...sec, rows: sec.rows.map((r,j) => j===ri ? { ...r, title: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g,'_') } : r) };
                    set('sections', next);
                  }} />
                <input className="form-input" placeholder="Description (optional)" style={{ flex: 1.5 }}
                  value={row.description ?? ''} onChange={e => {
                    const next = [...sections];
                    next[si] = { ...sec, rows: sec.rows.map((r,j) => j===ri ? { ...r, description: e.target.value } : r) };
                    set('sections', next);
                  }} />
                {(sec.rows?.length ?? 0) > 1 && (
                  <button className="icon-btn danger" onClick={() => {
                    const next = [...sections];
                    next[si] = { ...sec, rows: sec.rows.filter((_,j)=>j!==ri) };
                    set('sections', next);
                  }}>✕</button>
                )}
              </div>
            ))}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 8px', marginTop: 4 }}
              onClick={() => {
                const next = [...sections];
                next[si] = { ...sec, rows: [...(sec.rows??[]), { id:`row${Date.now()}`, title:'', description:'' }] };
                set('sections', next);
              }}>+ Add Row</button>
          </div>
        ))}
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
          onClick={() => set('sections', [...sections, { title:'', rows:[{ id:`row${Date.now()}`, title:'', description:'' }] }])}>
          + Add Section
        </button>
        <div className="form-hint">After saving, each row becomes a connectable output — connect rows to different next steps.</div>
      </div>
    );
  };

  return (
    <div className="fb-config-panel">
      <div className="fb-config-header" style={{ borderColor: color }}>
        <div className="fb-config-title"><span style={{ fontSize: 18 }}>{icon}</span><span>{label}</span></div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="icon-btn danger" title="Delete node" onClick={() => onDelete(node.id)}>🗑</button>
          <button className="icon-btn" title="Close" onClick={onClose}>✕</button>
        </div>
      </div>

      <div className="fb-config-body">
        {/* Description */}
        {desc && <div className="fb-config-desc">{desc}</div>}
        {howto && <div className="fb-config-howto">💡 {howto}</div>}

        {/* ── Trigger configs ──────────────────────────────── */}
        {subtype === 'whatsapp_message' && <>
          <CfgField cfg={cfg} set={set} label="Keyword" k="keyword" placeholder="hi, /start, or * for any message" />
          <div className="form-group">
            <label className="form-label">Match type</label>
            <select className="form-input" value={cfg.triggerType ?? 'exact'} onChange={e => set('triggerType', e.target.value)}>
              <option value="exact">Exact match</option>
              <option value="contains">Contains</option>
              <option value="regex">Regex</option>
              <option value="wildcard">Wildcard (*)</option>
            </select>
          </div>
        </>}

        {subtype === 'whatsapp_button' && (
          <ButtonTriggerPicker cfg={cfg} set={set} bId={bId} aId={aId} currentCanvasNodes={allNodes} />
        )}

        {subtype === 'schedule' && <>
          <div className="form-group">
            <label className="form-label">Frequency</label>
            <select className="form-input" value={cfg.frequency ?? 'daily'} onChange={e => set('frequency', e.target.value)}>
              <option value="hourly">Every hour</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="cron">Custom cron expression</option>
            </select>
          </div>
          {(cfg.frequency === 'daily' || !cfg.frequency) && <CfgField cfg={cfg} set={set} label="Time (HH:MM, 24h)" k="time" placeholder="09:00" />}
          {cfg.frequency === 'weekly' && <><CfgField cfg={cfg} set={set} label="Day of week" k="day" placeholder="Monday" /><CfgField cfg={cfg} set={set} label="Time (HH:MM)" k="time" placeholder="09:00" /></>}
          {cfg.frequency === 'cron' && <CfgField cfg={cfg} set={set} label="Cron expression" k="cron" placeholder="0 9 * * 1-5" hint="5-field standard cron. Example: '0 9 * * 1-5' = 9am Mon–Fri" />}
        </>}

        {subtype === 'webhook' && (
          <div className="fb-config-howto">
            💡 Save this automation to get a unique webhook URL. Paste it into Stripe, Shopify, your server, or any tool that can send HTTP requests. When it receives a POST, this automation fires. Access request data as <code>{'{{webhook.field}}'}</code>.
          </div>
        )}

        {subtype === 'manual' && (
          <div className="fb-config-howto">
            💡 No configuration needed. Go back to the Automations list and click "Run Now" to trigger this manually. Use it to test your flow before switching to a real trigger.
          </div>
        )}

        {subtype === 'payment_received' && (
          <div className="fb-config-howto">
            💰 No configuration needed here. Once you <strong>Activate</strong> this automation, it fires automatically after a customer completes a real payment from any <strong>Request Payment</strong> block on this bot.
            <div style={{ marginTop: 8 }}>
              Available in every step below:
              <div style={{ marginTop: 4 }}>
                <code>{'{{order.amount}}'}</code> · <code>{'{{order.description}}'}</code> · <code>{'{{order.id}}'}</code> · <code>{'{{chatId}}'}</code>
              </div>
            </div>
          </div>
        )}

        {/* ── WhatsApp action configs ───────────────────────── */}
        {subtype === 'send_text' && <>
          <CfgTextarea cfg={cfg} set={set} label="Message" k="text" placeholder={"Hello {{contact.name}}!\n\nHow can I help you today?"} rows={4} />
          <div className="form-hint">Variables: {'{{contact.name}}'} {'{{chatId}}'} {'{{storage.key}}'} {'{{now}}'}</div>
        </>}

        {(subtype === 'send_buttons' || subtype === 'send_buttons_image') && <>
          {subtype === 'send_buttons_image' && <>
            <CfgField cfg={cfg} set={set} label="Image URL" k="image_url" placeholder="https://example.com/banner.jpg" />
            <CfgField cfg={cfg} set={set} label="Image caption (optional)" k="image_caption" placeholder="Check out our menu!" />
          </>}
          <CfgTextarea cfg={cfg} set={set} label="Message body" k="body" placeholder="Choose an option below:" rows={2} />
          <CfgField cfg={cfg} set={set} label="Footer (optional)" k="footer" placeholder="Powered by CommandKit" />
          {renderButtonsBuilder()}
        </>}

        {subtype === 'send_cta_url' && <>
          <div className="fb-config-howto">
            💡 This sends a single button with an external-link icon (like "Sign Up/Login" in your screenshot) that opens a URL when tapped. WhatsApp does not allow combining this with reply buttons — it's always exactly one button.
          </div>
          <CfgField cfg={cfg} set={set} label="Header (optional)" k="header" placeholder="WapsChat PRO" />
          <CfgTextarea cfg={cfg} set={set} label="Message body" k="body" placeholder="Powerful WhatsApp CRM and automation suite..." rows={4} />
          <CfgField cfg={cfg} set={set} label="Button text" k="display_text" placeholder="Sign Up/Login"
            hint="Max 20 characters — shown on the button itself." />
          <CfgField cfg={cfg} set={set} label="URL" k="url" placeholder="https://wapschat.ng/pro-pricing" />
          <CfgField cfg={cfg} set={set} label="Footer (optional)" k="footer" placeholder="" />
        </>}

        {subtype === 'send_list' && <>
          <div className="fb-config-howto" style={{ borderLeftColor: '#e53e3e', background: 'rgba(229,62,62,0.07)', color: '#9b2335' }}>
            ⚠️ <strong>Important:</strong> After this node, connect an <strong>Ask Question</strong> block and set "Save answer as" to <code>selected_price</code> (or any name you like). This makes the bot <em>wait</em> for the user to pick from the list before continuing. Without it, the flow runs immediately without waiting.
          </div>
          <CfgTextarea cfg={cfg} set={set} label="Message body" k="body" placeholder="Please select an option from the list:" rows={2} />
          <CfgField cfg={cfg} set={set} label="Button label" k="buttonLabel" placeholder="View Options" />
          {renderListSectionsBuilder()}
        </>}

        {subtype === 'ask_question' && (() => {
          // Check if there's a Send List or Send Buttons node on canvas
          const hasListOrBtn = allNodes.some(n => n.data?.subtype === 'send_list' || n.data?.subtype === 'send_buttons' || n.data?.subtype === 'send_buttons_image');
          const listRows = allNodes
            .filter(n => n.data?.subtype === 'send_list')
            .flatMap(n => (n.data?.config?.sections ?? []).flatMap(s => s.rows ?? []))
            .filter(r => r.title).map(r => r.title);

          return <>
            {hasListOrBtn && (
              <div className="fb-config-howto" style={{ borderLeftColor: '#25D366', background: 'rgba(37,211,102,0.07)', color: '#1a6e3a' }}>
                ✅ Place this block right after your <strong>Send List</strong> or <strong>Send Buttons</strong>. It pauses the flow and waits for the user's reply before moving on. Leave "Question to ask" blank if you just want to wait silently.
              </div>
            )}
            <CfgTextarea cfg={cfg} set={set} label="Question to ask (optional — leave blank to wait silently)" k="question" placeholder="Leave blank to just wait for their list/button reply" rows={2} />
            <div className="form-group">
              <label className="form-label">Save reply as</label>
              <input className="form-input" placeholder="selected_price"
                value={cfg.save_as ?? ''} onChange={e => set('save_as', e.target.value)} />
              <div className="form-hint">
                Their reply will be stored as <code>{`{{storage.${cfg.save_as || 'selected_price'}}}`}</code> — use this in your If/Else to check what they picked.
                {listRows.length > 0 && <><br/>Your list options: <strong>{listRows.join(', ')}</strong></>}
              </div>
            </div>
            <CfgField cfg={cfg} set={set} label="Timeout (seconds)" k="timeout" placeholder="120" type="number"
              hint="How long to wait before giving up. Default: 120s." />
          </>;
        })()}

        {subtype === 'send_flow' && <>
          <CfgField cfg={cfg} set={set} label="WhatsApp Flow ID" k="flow_id" placeholder="1234567890"
            hint="Find this in Meta Business → WhatsApp → Flows." />
          <CfgField cfg={cfg} set={set} label="Header" k="header" placeholder="Get started" />
          <CfgTextarea cfg={cfg} set={set} label="Body" k="body" placeholder="Fill in the form below." rows={2} />
          <CfgField cfg={cfg} set={set} label="Button label" k="button_label" placeholder="Open Form" />
        </>}

        {subtype === 'send_template' && <>
          <CfgField cfg={cfg} set={set} label="Template name" k="name" placeholder="hello_world"
            hint="Must be an approved template in Meta Business Manager." />
          <CfgField cfg={cfg} set={set} label="Language code" k="language" placeholder="en_US" />
        </>}

        {subtype === 'send_media' && <>
          <div className="form-group">
            <label className="form-label">Media type</label>
            <select className="form-input" value={cfg.mediaType ?? 'image'} onChange={e => set('mediaType', e.target.value)}>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
              <option value="audio">Audio</option>
            </select>
          </div>
          <CfgField cfg={cfg} set={set} label="Media URL" k="url" placeholder="https://example.com/file.jpg" />
          <CfgField cfg={cfg} set={set} label="Caption (optional)" k="caption" placeholder="Here's your file!" />
        </>}

        {/* ── Commerce ──────────────────────────────────────── */}
        {subtype === 'request_payment' && (() => {
          const mode = cfg.mode === 'custom' ? 'custom' : 'product';
          return <>
            <div className="fb-config-howto">
              💳 Sends a real "Pay" button. Requires PocketFi to be connected for this bot in the <strong>Commerce</strong> tab first — money goes straight to your own PocketFi account, CmdKit never touches it.
            </div>

            <div className="form-group">
              <label className="form-label">What are they paying for?</label>
              <select className="form-input" value={mode} onChange={e => set('mode', e.target.value)}>
                <option value="product">A saved product</option>
                <option value="custom">Custom amount</option>
              </select>
            </div>

            {mode === 'product' ? (
              <div className="form-group">
                <label className="form-label">Product</label>
                <select
                  className="form-input"
                  value={cfg.product_id ?? ''}
                  onChange={e => {
                    const p = products.find(p => p.prId === e.target.value);
                    setMany({ product_id: e.target.value, product_name: p ? p.prName : '' });
                  }}
                >
                  <option value="">Select a product…</option>
                  {products.map(p => (
                    <option key={p.prId} value={p.prId}>
                      {p.prName} — ₦{Number(p.prPrice).toLocaleString('en-NG')}
                    </option>
                  ))}
                </select>
                {productsLoaded && products.length === 0 && (
                  <div className="form-hint">No products yet — add one in the Commerce tab first.</div>
                )}
              </div>
            ) : <>
              <CfgField cfg={cfg} set={set} label="Description" k="description" placeholder="2x Blue Sneakers" />
              <CfgField cfg={cfg} set={set} label="Amount (₦)" k="amount" placeholder="15000" type="number" />
            </>}

            <CfgTextarea cfg={cfg} set={set} label="Message body" k="body" placeholder="Tap below to complete your order:" rows={2} />
            <CfgField cfg={cfg} set={set} label="Button text (optional)" k="button_text"
              placeholder={cfg.amount ? `Pay ₦${cfg.amount}` : 'Pay ₦15,000'}
              hint="Leave blank to auto-generate from the amount." />
          </>;
        })()}


        {/* ── Logic ─────────────────────────────────────────── */}
        {subtype === 'if_else' && <IfElseConfig cfg={cfg} set={set} setMany={setMany} allNodes={allNodes} />}

        {subtype === 'switch' && (() => {
          // Same source detection for Switch
          const listNodes = allNodes.filter(n => n.data?.subtype === 'send_list');
          const btnNodes  = allNodes.filter(n => n.data?.subtype === 'send_buttons' || n.data?.subtype === 'send_buttons_image');
          const varNodes  = allNodes.filter(n => n.data?.subtype === 'set_var' && n.data?.config?.key);
          const askNodes  = allNodes.filter(n => n.data?.subtype === 'ask_question' && n.data?.config?.save_as);
          const sources = [];
          listNodes.forEach(n => { const b = n.data?.config?.body?.slice(0,24); sources.push({ label: `List: "${b||'Send List'}"`, variable: '{{message}}' }); });
          btnNodes.forEach(n => { const b = n.data?.config?.body?.slice(0,24); sources.push({ label: `Buttons: "${b||'Send Buttons'}"`, variable: '{{message}}' }); });
          askNodes.forEach(n => sources.push({ label: `Answer: ${n.data.config.save_as}`, variable: `{{storage.${n.data.config.save_as}}}` }));
          varNodes.forEach(n => sources.push({ label: `Variable: ${n.data.config.key}`, variable: `{{storage.${n.data.config.key}}}` }));
          sources.push({ label: '✏️ Type manually', variable: '' });

          return <>
            <div className="fb-config-howto">💡 Each case below becomes a separate output connector. Connect each one to a different next step. "Default" runs when no case matches.</div>
            <div className="form-group">
              <label className="form-label">What are you checking?</label>
              {sources.length > 1 ? (
                <select className="form-input" value={cfg.left ?? ''} onChange={e => set('left', e.target.value)}>
                  <option value="">— select —</option>
                  {sources.map((s, i) => <option key={i} value={s.variable}>{s.label}</option>)}
                </select>
              ) : (
                <input className="form-input" placeholder="{{message}} or {{storage.key}}"
                  value={cfg.left ?? ''} onChange={e => set('left', e.target.value)} />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Cases — each becomes a connectable output</label>
              {(cfg.cases ?? [{ value: '', label: 'Case 1' }]).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="form-input" placeholder={`e.g. 500`} value={c.value ?? ''} onChange={e => {
                    const next = [...(cfg.cases ?? [])];
                    next[i] = { ...c, value: e.target.value, label: e.target.value || `Case ${i+1}` };
                    set('cases', next);
                  }} />
                  {(cfg.cases ?? []).length > 1 && (
                    <button className="icon-btn danger" onClick={() => set('cases', (cfg.cases??[]).filter((_,j)=>j!==i))}>✕</button>
                  )}
                </div>
              ))}
              <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 10px' }}
                onClick={() => set('cases', [...(cfg.cases??[]), { value:'', label:`Case ${(cfg.cases??[]).length+1}` }])}>
                + Add Case
              </button>
            </div>
          </>;
        })()}

        {subtype === 'set_var' && <>
          <CfgField cfg={cfg} set={set} label="Variable name" k="key" placeholder="step"
            hint={'Access later as {{storage.step}} in any message or condition.'} />
          <CfgField cfg={cfg} set={set} label="Value" k="value" placeholder="{{message}} or any text" />
        </>}

        {subtype === 'wait' && <CfgField cfg={cfg} set={set} label="Seconds to wait" k="seconds" placeholder="2" type="number" />}

        {subtype === 'end' && (
          <div className="fb-config-howto">💡 The flow stops here. Useful to terminate one branch of an If/Else without affecting other branches.</div>
        )}

        {/* ── Integrations ──────────────────────────────────── */}
        {subtype === 'send_email' && <>
          <CfgField cfg={cfg} set={set} label="To" k="to" placeholder="admin@example.com or {{storage.email}}" />
          <CfgField cfg={cfg} set={set} label="Subject" k="subject" placeholder="New lead: {{contact.name}}" />
          <CfgTextarea cfg={cfg} set={set} label="Body" k="body" placeholder={"Hi team,\n\nNew signup:\nName: {{storage.user_name}}\nPhone: {{chatId}}"} rows={5} />
        </>}

        {subtype === 'http_post' && <>
          <div className="form-group">
            <label className="form-label">Method</label>
            <select className="form-input" value={cfg.method ?? 'POST'} onChange={e => set('method', e.target.value)}>
              <option value="POST">POST</option>
              <option value="GET">GET</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
            </select>
          </div>
          <CfgField cfg={cfg} set={set} label="URL" k="url" placeholder="https://api.example.com/endpoint" />
          <CfgTextarea cfg={cfg} set={set} label="Body (JSON)" k="body_json" rows={4}
            placeholder={'{\n  "phone": "{{chatId}}",\n  "name": "{{contact.name}}"\n}'} />
          <CfgTextarea cfg={cfg} set={set} label="Headers (JSON, optional)" k="headers_json" rows={2}
            placeholder={'{"Authorization": "Bearer {{panel.token}}"}'} />
          <CfgField cfg={cfg} set={set} label="Save response as" k="save_as" placeholder="api_result"
            hint={'Access fields as {{api_result.field}} in later steps.'} />
        </>}

        {subtype === 'google_sheets' && <>
          <div className="form-group">
            <label className="form-label">Action</label>
            <select className="form-input" value={cfg.action ?? 'append'} onChange={e => set('action', e.target.value)}>
              <option value="append">Append row</option>
              <option value="read">Read cell/range</option>
            </select>
          </div>
          <CfgField cfg={cfg} set={set} label="Spreadsheet ID" k="sheet_id" placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74…"
            hint="Copy from the Google Sheets URL between /d/ and /edit" />
          <CfgField cfg={cfg} set={set} label="Sheet tab name" k="sheet_name" placeholder="Sheet1" />
          {cfg.action !== 'read'
            ? <CfgTextarea cfg={cfg} set={set} label="Row values (comma-separated)" k="row" rows={2}
                placeholder={"{{storage.user_name}},{{chatId}},{{storage.plan}},{{now}}"} />
            : <><CfgField cfg={cfg} set={set} label="Range" k="range" placeholder="A2:B10" /><CfgField cfg={cfg} set={set} label="Save result as" k="save_as" placeholder="sheet_data" /></>
          }
        </>}

        {subtype === 'airtable' && <>
          <CfgField cfg={cfg} set={set} label="API Key" k="api_key" placeholder="patXXXXXXXXX.xxxxxx" />
          <CfgField cfg={cfg} set={set} label="Base ID" k="base_id" placeholder="appXXXXXXXXX" />
          <CfgField cfg={cfg} set={set} label="Table name" k="table" placeholder="Leads" />
          <div className="form-group">
            <label className="form-label">Action</label>
            <select className="form-input" value={cfg.action ?? 'create'} onChange={e => set('action', e.target.value)}>
              <option value="create">Create record</option>
              <option value="update">Update record</option>
            </select>
          </div>
          <CfgTextarea cfg={cfg} set={set} label="Fields (JSON)" k="fields_json" rows={3}
            placeholder={'{\n  "Name": "{{storage.user_name}}",\n  "Phone": "{{chatId}}"\n}'} />
          <CfgField cfg={cfg} set={set} label="Save result as" k="save_as" placeholder="airtable_result" />
        </>}

        {subtype === 'slack' && <>
          <CfgField cfg={cfg} set={set} label="Slack Webhook URL" k="webhook_url" placeholder="https://hooks.slack.com/services/…"
            hint="Get this from Slack → Apps → Incoming Webhooks." />
          <CfgField cfg={cfg} set={set} label="Channel (optional)" k="channel" placeholder="#leads" />
          <CfgTextarea cfg={cfg} set={set} label="Message" k="text" rows={3}
            placeholder={"New lead: {{contact.name}}\nPhone: {{chatId}}\nPlan: {{storage.plan}}"} />
        </>}

        {subtype === 'openai' && <>
          <CfgField cfg={cfg} set={set} label="OpenAI API Key" k="api_key" placeholder="sk-…" />
          <CfgTextarea cfg={cfg} set={set} label="System prompt" k="system" rows={3}
            placeholder={"You are a helpful assistant for a Nigerian fintech company.\nBe concise and friendly."} />
          <CfgTextarea cfg={cfg} set={set} label="User message" k="prompt" rows={2} placeholder="{{message}}" />
          <CfgField cfg={cfg} set={set} label="Model" k="model" placeholder="gpt-4o-mini" />
          <CfgField cfg={cfg} set={set} label="Max tokens" k="max_tokens" placeholder="300" type="number" />
          <CfgField cfg={cfg} set={set} label="Save reply as" k="save_as" placeholder="ai_reply"
            hint={'Send it with: {{storage.ai_reply}}'} />
        </>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  PREVIEW MODAL — live WhatsApp-style chat simulator
// ─────────────────────────────────────────────────────────────
//  Walks the ACTUAL canvas graph (not a mock) — same edge/handle
//  convention compiler.js uses (`${nodeId}::${handle}`), same
//  condition logic ConditionTester already uses (evalOpForPreview),
//  so If/Else and Switch branches behave identically to production.
//  Nodes that call out to WhatsApp/HTTP/etc. can't actually fire
//  from the browser, so those render as a labelled system note
//  instead of a chat bubble — everything else (text, buttons, lists,
//  ask_question, request_payment's "must pay before continuing"
//  behavior, storage variables) is genuinely simulated end-to-end.
// ─────────────────────────────────────────────────────────────
function resolvePreviewTemplate(vars, template) {
  if (!template) return '';
  return String(template).replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
    const key = raw.trim();
    const val = vars[key];
    return val !== undefined && val !== null ? String(val) : '';
  });
}

function PreviewModal({ nodes, edges, onClose }) {
  const nodeMap = useMemo(() => Object.fromEntries(nodes.map(n => [n.id, n])), [nodes]);
  const edgeMap = useMemo(() => {
    const m = {};
    edges.forEach(e => {
      const srcNode = nodeMap[e.source];
      const fallback = srcNode?.type === 'triggerNode' ? 'out' : 'default';
      const handle = e.sourceHandle || fallback;
      const key = `${e.source}::${handle}`;
      (m[key] ??= []).push(e.target);
    });
    return m;
  }, [edges, nodeMap]);

  const [log, setLog]         = useState([]);
  const [pending, setPending] = useState(null); // { kind:'ask'|'options'|'pay', nodeId, options? }
  const [ended, setEnded]     = useState(false);
  const [typing, setTyping]   = useState(false);
  const [answerText, setAnswerText] = useState('');
  const varsRef  = useRef({});
  const stepsRef = useRef(0); // safety counter, resets whenever we pause for input
  const bodyEndRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => { bodyEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [log, pending, typing]);
  useEffect(() => { if (!startedRef.current) { startedRef.current = true; start(); } }, []); // eslint-disable-line

  function push(entry) {
    setLog(l => [...l, { ...entry, _k: `${Date.now()}_${Math.random().toString(36).slice(2)}` }]);
  }

  function start() {
    varsRef.current = { chatId: '2348012345678', 'contact.name': 'Test Customer', name: 'Test Customer', now: new Date().toLocaleString() };
    setLog([]); setPending(null); setEnded(false); stepsRef.current = 0;

    const trigger = nodes.find(n => n.type === 'triggerNode');
    if (!trigger) {
      push({ kind: 'system', text: '⚠️ Add a trigger block to preview this automation.' });
      setEnded(true);
      return;
    }
    const sub = trigger.data?.triggerType ?? trigger.data?.subtype;
    let introText = '🧪 Simulating "Run Now"…';
    if (sub === 'whatsapp_message') {
      const kw = trigger.data?.config?.keyword;
      const msg = (kw && kw !== '*') ? kw : 'hello';
      varsRef.current.message = msg;
      introText = `🧪 Simulating a customer sending: "${msg}"`;
    } else if (sub === 'whatsapp_button') {
      const kw = trigger.data?.config?.keyword || 'button_id';
      varsRef.current.message = kw;
      introText = `🧪 Simulating a customer tapping button ID: "${kw}"`;
    } else if (sub === 'payment_received') {
      varsRef.current['order.id']          = 'ORD1A2B3C4D5E';
      varsRef.current['order.amount']      = '15000';
      varsRef.current['order.description'] = 'Sample Product';
      introText = '🧪 Simulating a successful ₦15,000 payment';
    } else if (sub === 'schedule') {
      introText = '🧪 Simulating the scheduled time arriving';
    } else if (sub === 'webhook') {
      introText = '🧪 Simulating an incoming webhook call';
    }
    push({ kind: 'system', text: introText });
    setTyping(true);
    setTimeout(() => advance(trigger.id, 'out'), 500);
  }

  // Follow one edge forward and process whatever node it lands on.
  function advance(fromNodeId, handle) {
    if (++stepsRef.current > 60) {
      setTyping(false);
      push({ kind: 'system', text: '⚠️ Stopped — this looks like a loop with no pause step (add an Ask Question, buttons, or a list to break the cycle).' });
      setEnded(true);
      return;
    }
    const targets = edgeMap[`${fromNodeId}::${handle}`] || [];
    if (targets.length === 0) {
      setTyping(false);
      push({ kind: 'system', text: '— End of this branch (nothing connected here) —' });
      setEnded(true);
      return;
    }
    processNode(targets[0]);
  }

  function processNode(nodeId) {
    const node = nodeMap[nodeId];
    if (!node) { setTyping(false); setEnded(true); return; }
    const cfg = node.data?.config ?? {};
    const subtype = node.data?.subtype;
    const r = (tpl) => resolvePreviewTemplate(varsRef.current, tpl);

    const DELAY = 550;
    setTyping(true);

    setTimeout(() => {
      setTyping(false);

      switch (subtype) {
        case 'send_text':
          push({ kind: 'bot', text: r(cfg.text) || '(empty message)' });
          advance(nodeId, 'default');
          break;

        case 'send_buttons':
        case 'send_buttons_image': {
          const btns = (cfg.buttons ?? []).filter(b => b.title);
          push({ kind: 'bot', text: r(cfg.body) || '(empty message)', options: btns.map(b => b.title) });
          if (btns.length === 0) { advance(nodeId, 'default'); break; }
          setPending({ kind: 'options', nodeId, options: btns.map(b => b.title) });
          break;
        }

        case 'send_list': {
          const rows = (cfg.sections ?? []).flatMap(s => s.rows ?? []).filter(r2 => r2.title);
          push({ kind: 'bot', text: r(cfg.body) || '(empty message)', options: rows.map(r2 => r2.title), listStyle: true });
          if (rows.length === 0) { advance(nodeId, 'default'); break; }
          setPending({ kind: 'options', nodeId, options: rows.map(r2 => r2.title) });
          break;
        }

        case 'send_cta_url':
          push({ kind: 'bot', text: r(cfg.body) || '(empty message)', ctaLabel: r(cfg.display_text) || 'Open Link' });
          advance(nodeId, 'default');
          break;

        case 'send_media':
          push({ kind: 'bot', text: `📎 [${cfg.mediaType ?? 'image'}] ${r(cfg.caption) || r(cfg.url) || '(no caption)'}` });
          advance(nodeId, 'default');
          break;

        case 'send_flow':
          push({ kind: 'system', text: `📱 Would open WhatsApp Flow "${cfg.flow_id || '(not set)'}" — can't be simulated here, continuing…` });
          advance(nodeId, 'default');
          break;

        case 'send_template':
          push({ kind: 'system', text: `📄 Would send approved template "${cfg.name || '(not set)'}"` });
          advance(nodeId, 'default');
          break;

        case 'ask_question':
          if (r(cfg.question)) push({ kind: 'bot', text: r(cfg.question) });
          setPending({ kind: 'ask', nodeId, saveAs: cfg.save_as || '' });
          break;

        case 'request_payment': {
          const mode = cfg.mode === 'custom' ? 'custom' : 'product';
          const amount = mode === 'custom' ? (cfg.amount || 0) : '';
          const label = mode === 'custom'
            ? (cfg.description || 'Custom amount')
            : (cfg.product_name || 'Selected product');
          push({ kind: 'bot', text: r(cfg.body) || 'Tap below to complete your payment:', payLabel: cfg.button_text || `Pay${amount ? ` ₦${Number(amount).toLocaleString('en-NG')}` : ''}`, payDesc: label });
          setPending({ kind: 'pay', nodeId, amount, label });
          break;
        }

        case 'if_else': {
          const left  = r(cfg.left);
          const right = r(cfg.right);
          const pass  = evalOpForPreview(left, cfg.op ?? '==', right);
          push({ kind: 'system', text: `🔀 Checked: "${left}" ${cfg.op ?? '=='} "${right}" → ${pass ? '✓ Yes' : '✗ No'}` });
          advance(nodeId, pass ? '✓ Yes' : '✗ No');
          break;
        }

        case 'switch': {
          const left = r(cfg.left);
          const cases = cfg.cases ?? [];
          const match = cases.find(c => (c.value ?? '') === left);
          const handle = match ? (match.label || match.value) : 'Default';
          push({ kind: 'system', text: `🔁 Checked "${left}" → ${match ? `matched "${handle}"` : 'no match, using Default'}` });
          advance(nodeId, handle);
          break;
        }

        case 'set_var':
          varsRef.current[`storage.${cfg.key}`] = r(cfg.value);
          push({ kind: 'system', text: `🗄 storage.${cfg.key || '?'} = "${r(cfg.value)}"` });
          advance(nodeId, 'default');
          break;

        case 'wait':
          push({ kind: 'system', text: `⏱ Waits ${cfg.seconds || 0}s…` });
          advance(nodeId, 'default');
          break;

        case 'end':
          push({ kind: 'system', text: '— 🔚 Flow ended —' });
          setEnded(true);
          break;

        case 'send_email':
          push({ kind: 'system', text: `📧 Would email ${r(cfg.to) || '(no recipient set)'}: "${r(cfg.subject)}"` });
          advance(nodeId, 'default');
          break;

        case 'http_post':
          push({ kind: 'system', text: `🌐 Would ${cfg.method ?? 'POST'} → ${r(cfg.url) || '(no URL set)'}` });
          if (cfg.save_as) varsRef.current[cfg.save_as] = '(simulated response)';
          advance(nodeId, 'default');
          break;

        case 'google_sheets':
          push({ kind: 'system', text: `📊 Would ${cfg.action === 'read' ? 'read from' : 'append a row to'} Google Sheet "${cfg.sheet_id ? cfg.sheet_id.slice(0, 12) + '…' : '(not set)'}"` });
          advance(nodeId, 'default');
          break;

        case 'airtable':
          push({ kind: 'system', text: `🟦 Would ${cfg.action === 'update' ? 'update' : 'create'} a record in Airtable table "${cfg.table || '(not set)'}"` });
          advance(nodeId, 'default');
          break;

        case 'slack':
          push({ kind: 'system', text: `💼 Would post to Slack: "${r(cfg.text) || '(empty message)'}"` });
          advance(nodeId, 'default');
          break;

        case 'openai':
          push({ kind: 'system', text: '🤖 Would ask AI and save the reply' });
          if (cfg.save_as) varsRef.current[`storage.${cfg.save_as}`] = '(simulated AI reply)';
          advance(nodeId, 'default');
          break;

        default:
          push({ kind: 'system', text: `⚙️ (${subtype || 'unknown'} — not simulated)` });
          advance(nodeId, 'default');
      }
    }, DELAY);
  }

  function handleOptionTap(opt) {
    if (!pending || pending.kind !== 'options') return;
    push({ kind: 'user', text: opt });
    varsRef.current.message = opt;
    const nodeId = pending.nodeId;
    setPending(null);
    stepsRef.current = 0;
    advance(nodeId, opt);
  }

  function handleAskSubmit(e) {
    e.preventDefault();
    if (!pending || pending.kind !== 'ask' || !answerText.trim()) return;
    const text = answerText.trim();
    push({ kind: 'user', text });
    varsRef.current.message = text;
    if (pending.saveAs) varsRef.current[`storage.${pending.saveAs}`] = text;
    const nodeId = pending.nodeId;
    setAnswerText('');
    setPending(null);
    stepsRef.current = 0;
    advance(nodeId, 'default');
  }

  function handlePayTap() {
    if (!pending || pending.kind !== 'pay') return;
    push({ kind: 'system', text: `✅ Payment received! Payment confirmed for order.` });
    const nodeId = pending.nodeId;
    setPending(null);
    stepsRef.current = 0;
    advance(nodeId, 'default');
  }

  return (
    <div className="fb-preview-backdrop" onClick={onClose}>
      <div className="fb-preview-modal" onClick={e => e.stopPropagation()}>
        <div className="fb-preview-header">
          <div className="fb-preview-title">💬 Automation Preview</div>
          <div className="fb-preview-actions">
            <button className="fb-preview-icon-btn" title="Restart" onClick={start}>↻</button>
            <button className="fb-preview-icon-btn" title="Close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="fb-preview-body">
          {log.map(entry => (
            <div key={entry._k} className={`fb-pv-bubble fb-pv-${entry.kind}`}>
              {entry.text}
              {entry.options && (
                <div className="fb-pv-options">
                  {entry.options.map((o, i) => (
                    <div key={i} className="fb-pv-opt-hint">
                      {entry.listStyle ? '☰ ' : '▫ '}{o}
                    </div>
                  ))}
                </div>
              )}
              {entry.ctaLabel && <div className="fb-pv-opt-hint">🔗 {entry.ctaLabel}</div>}
              {entry.payLabel && (
                <div className="fb-pv-opt-hint" style={{ color: '#00a372', fontWeight: 800 }}>
                  💳 {entry.payLabel}{entry.payDesc ? ` — ${entry.payDesc}` : ''}
                </div>
              )}
            </div>
          ))}

          {typing && (
            <div className="fb-pv-bubble fb-pv-bot fb-typing-dots">
              <span>●</span><span>●</span><span>●</span>
            </div>
          )}

          {pending?.kind === 'options' && (
            <div className="fb-pv-options" style={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
              {pending.options.map((o, i) => (
                <button key={i} className="fb-pv-opt-btn" onClick={() => handleOptionTap(o)}>{o}</button>
              ))}
            </div>
          )}

          {pending?.kind === 'pay' && (
            <button className="fb-pv-pay-btn" style={{ alignSelf: 'flex-end', maxWidth: '78%' }} onClick={handlePayTap}>
              💳 Simulate Payment
            </button>
          )}

          {ended && (
            <div className="fb-pv-bubble fb-pv-system" style={{ fontWeight: 700 }}>— Preview finished —</div>
          )}

          <div ref={bodyEndRef} />
        </div>

        {pending?.kind === 'ask' ? (
          <form className="fb-preview-footer" onSubmit={handleAskSubmit}>
            <input autoFocus placeholder="Type a reply…" value={answerText} onChange={e => setAnswerText(e.target.value)} />
            <button type="submit" disabled={!answerText.trim()} title="Send">➤</button>
          </form>
        ) : (
          <div className="fb-preview-restart-bar">
            {ended
              ? <button className="btn btn-primary" onClick={start}>↻ Run again</button>
              : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Simulating live — nothing is actually sent</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  MAIN FlowBuilderPage
// ─────────────────────────────────────────────────────────────
export default function FlowBuilderPage() {
  const { bId, aId } = useParams();
  const navigate     = useNavigate();

  const [automation,   setAutomation]   = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [infoNode,     setInfoNode]     = useState(null);
  const [showHowTo,    setShowHowTo]    = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [activating,   setActivating]   = useState(false);
  const [name,         setName]         = useState('');
  const [loading,      setLoading]      = useState(true);
  const rfWrapRef  = useRef(null);
  const [rfInstance, setRfInstance]     = useState(null);

  // ── Undo / redo history ─────────────────────────────────────
  // Tracks structural changes only (add/remove node, add/remove edge,
  // node moved, auto-layout) — not every keystroke inside a config field,
  // so Ctrl+Z steps through meaningful actions rather than characters.
  const historyRef = useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = useState(0); // bump to re-render undo/redo button state

  const pushHistory = useCallback((prevNodes, prevEdges) => {
    historyRef.current.past.push({
      nodes: JSON.parse(JSON.stringify(prevNodes)),
      edges: JSON.parse(JSON.stringify(prevEdges)),
    });
    if (historyRef.current.past.length > 50) historyRef.current.past.shift();
    historyRef.current.future = [];
    setHistoryTick(t => t + 1);
  }, []);

  useEffect(() => {
    getAutomation(bId, aId).then(res => {
      const a = res.data?.automation;
      if (!a) { toast.error('Not found'); navigate(`/bots/${bId}/automations`); return; }
      setAutomation(a);
      setName(a.aName ?? '');
      if (a.aCanvas?.nodes?.length) {
        setNodes(a.aCanvas.nodes);
        setEdges(a.aCanvas.edges ?? []);
      }
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [bId, aId]);

  const onConnect = useCallback(params => {
    pushHistory(nodes, edges);
    setEdges(eds => addEdge({ ...params, ...EDGE_OPTS }, eds));
  }, [setEdges, nodes, edges, pushHistory]);

  // Block self-loops and exact duplicate connections (same source+handle → same target)
  // so the canvas can't silently end up with a connection that would never compile
  // to anything meaningful, or a redundant duplicate that's easy to miss visually.
  const isValidConnection = useCallback((conn) => {
    if (conn.source === conn.target) return false;
    const dup = edges.some(e =>
      e.source === conn.source && e.target === conn.target &&
      (e.sourceHandle ?? null) === (conn.sourceHandle ?? null)
    );
    return !dup;
  }, [edges]);

  const handleDeleteEdge = useCallback((edgeId) => {
    pushHistory(nodes, edges);
    setEdges(eds => eds.filter(e => e.id !== edgeId));
  }, [setEdges, nodes, edges, pushHistory]);

  // Inject the delete handler at render time only — never persisted into
  // `edges` state itself, since that's what gets saved to the backend and
  // functions aren't serializable.
  const edgesWithHandlers = useMemo(() =>
    edges.map(e => ({ ...e, data: { ...e.data, onDelete: handleDeleteEdge } })),
    [edges, handleDeleteEdge]
  );

  function handleAutoLayout() {
    pushHistory(nodes, edges);
    setNodes(nds => autoLayoutNodes(nds, edges));
    // Give React Flow a tick to apply new positions before recentring the view
    setTimeout(() => rfInstance?.fitView({ padding: 0.2, duration: 300 }), 50);
  }

  function handleUndo() {
    const { past } = historyRef.current;
    if (past.length === 0) return;
    const prev = past.pop();
    historyRef.current.future.push({
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    });
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNode(null);
    setHistoryTick(t => t + 1);
  }

  function handleRedo() {
    const { future } = historyRef.current;
    if (future.length === 0) return;
    const next = future.pop();
    historyRef.current.past.push({
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    });
    setNodes(next.nodes);
    setEdges(next.edges);
    setSelectedNode(null);
    setHistoryTick(t => t + 1);
  }

  // Wrap the raw React Flow change handlers so we only snapshot on
  // structural moments (a drag finishing, a node/edge actually being
  // removed) rather than on every intermediate pixel of a drag.
  const handleNodesChange = useCallback((changes) => {
    const structural = changes.some(c =>
      c.type === 'remove' || (c.type === 'position' && c.dragging === false)
    );
    if (structural) pushHistory(nodes, edges);
    onNodesChange(changes);
  }, [nodes, edges, onNodesChange, pushHistory]);

  const handleEdgesChange = useCallback((changes) => {
    const structural = changes.some(c => c.type === 'remove');
    if (structural) pushHistory(nodes, edges);
    onEdgesChange(changes);
  }, [nodes, edges, onEdgesChange, pushHistory]);

  // ── FIX: correct drop position calculation ─────────────────
  const onDrop = useCallback(e => {
    e.preventDefault();
    const type    = e.dataTransfer.getData('ck/type');
    const subtype = e.dataTransfer.getData('ck/subtype');
    const label   = e.dataTransfer.getData('ck/label');
    if (!type || !rfInstance) return;

    pushHistory(nodes, edges);

    // screenToFlowPosition takes screen coords directly — no manual offset needed
    const position = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });

    setNodes(nds => [...nds, {
      id:       `${subtype}-${Date.now()}`,
      type,
      position,
      data:     { label, subtype, triggerType: subtype, config: {} },
    }]);
  }, [rfInstance, setNodes, nodes, edges, pushHistory]);

  const onDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodeClick  = useCallback((_, node) => setSelectedNode(node), []);
  const onPaneClick  = useCallback(() => setSelectedNode(null), []);

  function handleConfigChange(nodeId, newCfg) {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, config: newCfg } } : n
    ));
    setSelectedNode(prev =>
      prev?.id === nodeId ? { ...prev, data: { ...prev.data, config: newCfg } } : prev
    );
  }

  function handleDeleteNode(nodeId) {
    pushHistory(nodes, edges);
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await saveAutomation({ bId, aId, name, canvas: { nodes, edges }, compiled: compileAutomation(nodes, edges) });
      if (res.data?.success) toast.success('Saved!');
      else toast.error(res.data?.message ?? 'Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  async function handleToggleActive() {
    setActivating(true);
    try {
      const active = automation?.aStatus === 'active';
      if (active) {
        await deactivateAutomation(bId, aId);
        setAutomation(a => ({ ...a, aStatus: 'draft' }));
        toast.success('Paused');
      } else {
        await saveAutomation({ bId, aId, name, canvas: { nodes, edges }, compiled: compileAutomation(nodes, edges) });
        const res = await activateAutomation(bId, aId);
        if (res.data?.success) { setAutomation(a => ({ ...a, aStatus: 'active' })); toast.success('Activated!'); }
        else toast.error(res.data?.message ?? 'Failed');
      }
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Failed'); }
    setActivating(false);
  }

  const isActive = automation?.aStatus === 'active';

  // ── Keyboard shortcuts ───────────────────────────────────────
  // Delete/Backspace for removing a selected node or edge is handled by
  // React Flow itself (see deleteKeyCode prop below).
  //
  // Ctrl/Cmd+S (save) and Ctrl/Cmd+P (activate/pause) are browser-reserved
  // combos (native "Save Page" / "Print"), so this listener is attached in
  // the CAPTURE phase with { passive: false } and calls BOTH
  // preventDefault() and stopPropagation() on them — that combination is
  // what actually stops Chrome/Firefox/Edge/Safari from opening their
  // native dialogs (this is the same pattern apps like Google Docs and
  // Notion use). Undo/redo are skipped while the user is typing in a text
  // field (name, config panel, etc.) so the browser's normal text-undo
  // still works there instead of jumping the whole canvas back a step.
  //
  // The handlers are called through refs (updated every render below)
  // rather than being direct dependencies of this effect, so the listener
  // itself is attached exactly ONCE for the life of the page. Re-attaching
  // it on every render (as a naive `[handleSave, handleToggleActive, ...]`
  // dependency array would do, since those are plain functions re-created
  // each render) opens a brief window on every re-render where the old
  // listener has been removed but the new one hasn't landed yet — that's
  // the kind of gap that can let a keystroke slip through to the browser.
  const handleSaveRef         = useRef(handleSave);
  const handleToggleActiveRef = useRef(handleToggleActive);
  const handleUndoRef         = useRef(handleUndo);
  const handleRedoRef         = useRef(handleRedo);
  useEffect(() => { handleSaveRef.current = handleSave; });
  useEffect(() => { handleToggleActiveRef.current = handleToggleActive; });
  useEffect(() => { handleUndoRef.current = handleUndo; });
  useEffect(() => { handleRedoRef.current = handleRedo; });

  useEffect(() => {
    function isTypingTarget(el) {
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    }
    function onKeyDown(e) {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();

      if (key === 's') {
        e.preventDefault();
        e.stopPropagation();
        handleSaveRef.current();
        return;
      }
      if (key === 'p') {
        e.preventDefault();
        e.stopPropagation();
        handleToggleActiveRef.current();
        return;
      }

      if (isTypingTarget(document.activeElement)) return;

      if (key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndoRef.current(); return; }
      if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); handleRedoRef.current(); return; }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []); // ← attached once; refs above always call the latest handlers

  if (loading) return <div className="fb-loading"><span className="spinner spinner-dark"/> Loading builder…</div>;

  return (
    <div className="fb-root">
      {/* Top bar */}
      <div className="fb-topbar">
        <button className="fb-back-btn" onClick={() => navigate(`/bots/${bId}/automations`)}>
          ← <span className="btn-label">Automations</span>
        </button>
        <input className="fb-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Automation name" />
        <div className="fb-topbar-right">
          <button className="fb-howto-btn" onClick={() => setShowHowTo(true)}>
            🧭 <span className="btn-label">How to use</span>
          </button>
          <button className="fb-howto-btn" onClick={handleUndo}
            disabled={historyRef.current.past.length === 0}
            title="Undo (Ctrl+Z)">
            ↶ <span className="btn-label">Undo</span>
          </button>
          <button className="fb-howto-btn" onClick={handleRedo}
            disabled={historyRef.current.future.length === 0}
            title="Redo (Ctrl+Y)">
            ↷ <span className="btn-label">Redo</span>
          </button>
          <button className="fb-howto-btn" onClick={handleAutoLayout} title="Neatly re-arrange all nodes into columns">
            🧹 <span className="btn-label">Auto-arrange</span>
          </button>
          <button className="fb-howto-btn fb-preview-trigger-btn" onClick={() => setShowPreview(true)}
            disabled={nodes.length === 0} title="Preview this automation as a live chat">
            ▶ <span className="btn-label">Preview</span>
          </button>
          <div className={`fb-status-pill${isActive ? ' fb-status-active' : ' fb-status-draft'}`}>
            <span className="fb-status-dot" />{isActive ? 'Active' : 'Draft'}
          </div>
          <button className="btn btn-outline" onClick={handleSave} disabled={saving} title="Save (Ctrl+S)">
            {saving ? <><span className="spinner spinner-dark"/> <span className="btn-label">Saving…</span></> : <>💾 <span className="btn-label">Save</span></>}
          </button>
          <button className={`btn ${isActive ? 'btn-outline' : 'btn-primary'}`}
            onClick={handleToggleActive} disabled={activating} title="Activate / Pause (Ctrl+P)">
            {activating
              ? <><span className="spinner"/> <span className="btn-label">{isActive ? 'Pausing…' : 'Activating…'}</span></>
              : <>{isActive ? '⏸' : '▶'} <span className="btn-label">{isActive ? 'Pause' : 'Activate'}</span></>}
          </button>
        </div>
      </div>

      <div className="fb-body">
        <NodePalette onInfo={setInfoNode} />


        {/* Canvas */}
        <div className="fb-canvas-wrap" ref={rfWrapRef} onDragOver={onDragOver} onDrop={onDrop}>
          <ReactFlow
            nodes={nodes} edges={edgesWithHandlers}
            onNodesChange={handleNodesChange} onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick} onPaneClick={onPaneClick}
            onInit={setRfInstance}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            defaultEdgeOptions={EDGE_OPTS}
            connectOnClick={false}
            deleteKeyCode={['Delete', 'Backspace']}
            connectionLineStyle={{ stroke: 'var(--primary, #002ced)', strokeWidth: 2.5 }}
            fitView snapToGrid snapGrid={[16, 16]}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} color="#e2e8f0" />
            <Controls showInteractive={false} />
            {nodes.length === 0 && (
              <Panel position="top-center">
                <div className="fb-empty-hint">
                  ← Drag blocks from the left panel onto this canvas to start building
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onChange={handleConfigChange}
            onDelete={handleDeleteNode}
            onClose={() => setSelectedNode(null)}
            allNodes={nodes}
            bId={bId}
            aId={aId}
          />
        )}
      </div>

      {infoNode   && <BlockInfoModal node={infoNode} onClose={() => setInfoNode(null)} />}
      {showHowTo  && <HowToModal onClose={() => setShowHowTo(false)} />}
      {showPreview && <PreviewModal nodes={nodes} edges={edges} onClose={() => setShowPreview(false)} />}
    </div>
  );
}